/**
 * Query key factory — docs/spec/02 §5.
 *
 * Every key in the app is built here. The point is invalidation: `jobs.lists()`
 * is a prefix of every `jobs.list(filters)` key, so after a mutation one
 * `invalidateQueries({ queryKey: queryKeys.jobs.lists() })` refreshes the queue
 * under *all* filter combinations without knowing which ones are cached.
 *
 * Hand-written array literals scattered through features break that: one typo
 * and a mutation silently stops refreshing a screen, which reads to a user as
 * "the app didn't save my work".
 */
import type { JobListFilters } from '../api/jobs'

/**
 * Normalise filters into a stable, serialisable key part.
 *
 * Two things matter here. Key order — an object literal's key order is part of
 * its JSON, so `{status, page}` and `{page, status}` would be different cache
 * entries for the same query. And omission — `undefined` values are dropped so
 * "filter absent" and "filter explicitly unset" share one entry rather than
 * fetching twice.
 */
export function jobListKeyPart(filters: JobListFilters): Record<string, unknown> {
  const part: Record<string, unknown> = {}
  for (const key of Object.keys(filters).sort()) {
    const value = filters[key as keyof JobListFilters]
    if (value === undefined) continue
    if (typeof value === 'string' || typeof value === 'number') {
      part[key] = value
      continue
    }
    // The only remaining shape is the status array. Sorted so that selecting
    // the same filters in a different order is the same cache entry.
    if (value.length === 0) continue
    part[key] = [...value].sort()
  }
  return part
}

export const queryKeys = {
  jobs: {
    all: () => ['jobs'] as const,
    lists: () => ['jobs', 'list'] as const,
    list: (filters: JobListFilters) => ['jobs', 'list', jobListKeyPart(filters)] as const,
    details: () => ['jobs', 'detail'] as const,
    detail: (id: string) => ['jobs', 'detail', id] as const,
    assignees: () => ['jobs', 'assignees'] as const,
  },
  /**
   * Live-socket state (03 §3). Server-owned data, so it lives in the Query cache
   * like everything else (01 §3) — it is simply **pushed rather than fetched**:
   * no `queryFn` ever populates this key, only a `counts` frame.
   *
   * Distinct from the queue's own `total_count`, which counts one filtered page
   * of `/jobs`. This is the global `{open, in_progress, qa_check}` triple.
   */
  live: {
    counts: () => ['live', 'counts'] as const,
  },
  // The rest of the 02 §5 factory, declared up front so the stages that bind
  // these endpoints extend one table instead of inventing parallel key shapes.
  scans: {
    forJob: (jobId: string) => ['scans', 'job', jobId] as const,
  },
  labels: {
    /** Prefix — invalidating this refreshes every page of the history. */
    all: () => ['labels'] as const,
    page: (page: number) => ['labels', 'list', { page }] as const,
  },
  countries: () => ['countries'] as const,
  facilities: {
    search: (q: string) => ['facilities', 'search', q] as const,
  },
} as const
